import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { NotionSyncBanner } from "@/components/NotionSyncBanner";
import { formatNotionError } from "@/lib/notionError";
import { DatePicker, DateRangePicker } from "@/components/DatePicker";
import { useEscapeKey } from "@/hooks/useEscapeKey";
import { useClientsPickerQuery } from "@/hooks/usePortalQueries";
import { useAuth } from "@/auth/AuthContext";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

// Field configs are keyed exactly to the backend create-endpoint body keys AND to the
// live Notion field-options keys returned by GET /{entity}/field-options — nothing hardcoded
// from Notion's own option values, only the field list/order/labels are UI decisions.
const CONFIGS = {
  client: {
    title: "New Client",
    endpoint: "/v1/clients/",
    fields: [
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "status", label: "Status", kind: "select" },
      { key: "industry", label: "Industry", kind: "select" },
      { key: "type", label: "Type", kind: "select" },
      { key: "geography", label: "Geography", kind: "multiselect" },
      { key: "billing_currency", label: "Billing Currency", kind: "select" },
    ],
  },
  contact: {
    title: "New Contact",
    endpoint: "/v1/contacts/",
    needsClient: true,
    fields: [
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "role", label: "Role / Title", kind: "text" },
      { key: "email", label: "Email", kind: "text" },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "decision_power", label: "Decision Power", kind: "select" },
      { key: "status", label: "Status", kind: "select" },
    ],
  },
    project: {
    title: "New Project",
    endpoint: "/v1/projects/",
    needsClient: true,
    fields: [
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "status", label: "Status", kind: "select" },
      { key: "category", label: "Category", kind: "select" },
      { key: "currency", label: "Currency", kind: "select" },
      { key: "budget", label: "Budget", kind: "number" },
      { key: "shoot_dates", label: "Shoot Dates", kind: "daterange" },
      { key: "objective", label: "Objective", kind: "textarea" },
      { key: "deliverables", label: "Deliverables Summary", kind: "textarea" },
      { key: "target_audience", label: "Target Audience", kind: "text" },
      { key: "shoot_locations", label: "Shoot Location(s)", kind: "text" },
      // AI Project Intake Copilot Phase 1 (2026-02): pre-fills ["international"] here (never
      // silently applied — PM sees/can untoggle it right in this same list before Save).
      { key: "tags", label: "Tags", kind: "multiselect" },
      { key: "business_problem", label: "Business Problem / Notes", kind: "textarea" },
      { key: "primary_contact_id", label: "Client Point of Contact", kind: "relation", relationEntity: "contacts", editOnly: true, scopeToClient: true, allowQuickCreateContact: true },
      { key: "drive", label: "Resource Link (Drive)", kind: "url", placeholder: "https://drive.google.com/…" },
      // Phase 0 (2026-02): unblocks Crew/Assignments — AssignmentEngine.jsx refuses to let
      // anyone assign crew until a project has a Production Model. Production Partner only
      // ever renders when Production Model = "Production Partner Managed" (showIf).
      // AI Project Intake Copilot Phase 1 (2026-02) also needs this visible+settable at
      // CREATE time (not just edit) so the AI's recommendation lands immediately.
      { key: "production_model", label: "Production Model", kind: "select" },
      {
        key: "production_partner_id", label: "Production Partner", kind: "relation",
        relationEntity: "build/partner-network", editOnly: true,
        showIf: (values) => values.production_model === "Production Partner Managed",
      },
      // PM Ownership Model (2026-09): pm_user_id is a first-class Project field — required
      // (server-enforced, Phase A) once status reaches Won/Pre-Production/Production/Post
      // (Delivered is flagged, never blocking). Options come from GET /v1/projects/
      // pm-eligible-users (founder/partner/pm/coordinator only, active accounts).
      { key: "pm_user_id", label: "Project Manager", kind: "relation", relationEntity: "projects/pm-eligible-users" },
    ],
  },
  // Production Partners module (2026-02, Phase A). Field keys are 1:1 with the live Notion
  // property names on `partner_network` (normalized) — no alias entries were needed in
  // config/notion_aliases.json for the 7 new fields (Resource Link/Website/Agreement Start+
  // Expiry Date/Default Payment Terms/Preferred Partner/Time Zone/Default Currency) since
  // their normalized key already matches what we want to call them.
  partner: {
    title: "New Partner",
    endpoint: "/v1/partners/",
    fields: [
      { key: "name", label: "Partner Name", kind: "text", required: true },
      { key: "partner_type", label: "Partner Type", kind: "select" },
      { key: "status", label: "Status", kind: "select" },
      { key: "partner_geography", label: "Geography", kind: "select" },
      { key: "default_currency", label: "Default Currency", kind: "select" },
      { key: "preferred_partner", label: "Preferred Partner", kind: "checkbox" },
      { key: "city", label: "City", kind: "text" },
      { key: "country", label: "Country", kind: "text" },
      { key: "gst_number", label: "GST Number", kind: "text" },
      { key: "company_registration_no", label: "Company Registration No.", kind: "text" },
      { key: "login_emails", label: "Login Email (magic-link portal access)", kind: "text", placeholder: "partner@example.com" },
      { key: "email", label: "Email", kind: "text" },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "primary_contact", label: "Primary Contact", kind: "text" },
      { key: "website", label: "Website", kind: "url", placeholder: "https://…" },
      { key: "resource_link", label: "Resource Link (Drive)", kind: "url", placeholder: "https://drive.google.com/…" },
      { key: "default_payment_terms", label: "Default Payment Terms", kind: "text", placeholder: "e.g. Net 30" },
      { key: "time_zone", label: "Time Zone", kind: "text", placeholder: "e.g. Asia/Kolkata" },
      { key: "agreement_start_date", label: "Agreement Start Date", kind: "date" },
      { key: "agreement_expiry_date", label: "Agreement Expiry Date", kind: "date" },
      { key: "notes", label: "Notes", kind: "textarea" },
    ],
  },
};

/**
 * Slide-over "New Record" / "Edit Record" form — Client / Contact / Project.
 * Direct native create/edit (no approval card, per backend design): the Save click IS the
 * human action. On create success, hands the created record back via onCreated. On edit
 * success, hands the updated record back via onSaved — callers show a toast + refresh the
 * current detail page in place (no redirect).
 *
 * mode="edit" additionally needs:
 *   - recordId: the id to PATCH
 *   - initialValues: a plain, form-ready object pre-resolved by the caller (e.g. {status,
 *     budget, shoot_dates:{start,end}, primary_contact_id, ...}) — this component does zero
 *     Notion-relation resolution itself, callers who already loaded the detail page do that.
 *   - initialClientId/initialClientName: prefills the (still-editable) Client relation select.
 */
export function RecordFormModal({ type, open, onClose, defaultClientId, defaultClientName, onCreated, mode = "create", recordId, initialValues, initialSyncInfo, initialClientId, initialClientName, onSaved, initialCreateValues, onRequestAddContact }) {
  const { user } = useAuth();
  // Notion Sync Visibility (2026-10) — matches the REAL backend gate (admin.write, kernel/
  // permissions.py), not a wider "founder or partner" guess: a partner clicking this before
  // would hit a 403 the UI never warned them about.
  const canRetrySync = user?.role === "founder";
  const cfg = CONFIGS[type];
  const isEdit = mode === "edit";
  const entityLabel = cfg.title.replace("New ", "");
  const [values, setValues] = useState({});
  const [options, setOptions] = useState({});
  const [clientId, setClientId] = useState("");
  const [relationOptions, setRelationOptions] = useState({});
  const [saving, setSaving] = useState(false);
  useEscapeKey(onClose, open);

  // Priority A (3), Bavna round (2026-10) — "Edit Project > Save" on a project whose Notion
  // page was never created (sync.state=pending_create, remote_id=null) always 500s with
  // notion_write_failed/no_remote_id (dal.py's update_record — there's nothing to PATCH on
  // the Notion side yet). Same founder-only "Retry sync now" action as the Admin
  // Stuck Sync viewer (POST /v1/admin/stuck-sync/{id}/retry) — reused here so a founder
  // doesn't have to leave this form and hunt through Admin to unblock their own edit.
  const retrySyncNow = async () => {
    try {
      const r = await api.post(`/v1/admin/stuck-sync/${recordId}/retry`, { confirm: true });
      if (r.data.ok) toast.success("Synced to Notion — try saving your edit again");
      else toast.error(formatNotionError(r.data.error) || `Still not synced: ${r.data.error_reason || r.data.error || "unknown error"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code === "permission_denied" ? "Only a founder can retry this sync" : "Couldn't retry sync");
    }
  };

  // In edit mode the Client relation is always an editable dropdown (pre-filled with the
  // current client, but re-assignable). The "locked" display div is only for the quick
  // create-from-Client-360 flow (defaultClientId), never during edit.
  const showLockedClient = !!defaultClientId && !isEdit;
  // Wave 1 (2026-10, frontend data cache) — the clients picker is shared across every open
  // instance of this modal (cached 15min, reference-like); only fetches while actually needed.
  const { data: clients = [] } = useClientsPickerQuery(open && cfg.needsClient && !showLockedClient);

  useEffect(() => {
    if (!open) return;
    setValues(isEdit ? (initialValues || {}) : (initialCreateValues || {}));
    setClientId(isEdit ? (initialClientId || "") : (defaultClientId || ""));
    api.get(`${cfg.endpoint}field-options`).then((r) => setOptions(r.data || {})).catch(() => setOptions({}));
    const relEntities = [...new Set(cfg.fields.filter((f) => f.kind === "relation" && (!f.editOnly || isEdit)).map((f) => f.relationEntity))];
    relEntities.forEach((ent) => {
      // Nested entities (e.g. "build/partner-network") already spell out their own exact
      // path — never append the trailing slash that top-level list endpoints (contacts/,
      // clients/) expect.
      const url = ent.includes("/") ? `/v1/${ent}` : `/v1/${ent}/`;
      api.get(url).then((r) => setRelationOptions((prev) => ({ ...prev, [ent]: r.data.items || [] }))).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, mode, recordId]);

  if (!open) return null;

  const setField = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));
  const toggleMulti = (key, val) => setValues((prev) => {
    const cur = new Set(prev[key] || []);
    cur.has(val) ? cur.delete(val) : cur.add(val);
    return { ...prev, [key]: Array.from(cur) };
  });

  const save = async () => {
    const name = (values.name || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    if (cfg.needsClient && !clientId) { toast.error("Client is required"); return; }
    setSaving(true);
    try {
      const body = { ...values, name };
      if (cfg.needsClient) body.client_id = clientId;
      // Date/date-range pickers store {start,end}|iso-string|null locally — strip anything
      // without an actual picked start so the backend never receives an empty shell.
      cfg.fields.forEach((f) => {
        if (f.kind === "daterange" && (!body[f.key] || !body[f.key].start)) delete body[f.key];
        if (f.kind === "date" && !body[f.key]) delete body[f.key];
      });
      if (isEdit) {
        const r = await api.patch(`${cfg.endpoint}${recordId}`, body);
        const record = r.data.record;
        toast.success(`${entityLabel} updated`, { description: name });
        onSaved?.(record, body.client_id);
        onClose();
      } else {
        const r = await api.post(cfg.endpoint, body);
        const record = r.data.record;
        if (r.data.warning) {
          // Notion Sync Visibility (2026-10), ask #3 — an inline Notion create failure must
          // never look like a plain success: same silent-swallow root cause diagnosed for the
          // Trifilm/Hyderabad project (create_record_mongo_first() always returns ok:true).
          toast.warning(r.data.warning.message, {
            description: "It's saved here and will keep retrying — or a founder can retry it now from this project.",
            action: onCreated ? { label: "Open", onClick: () => onCreated(record, { openDetail: true }) } : undefined,
          });
        } else {
          toast.success(`${entityLabel} created`, {
            description: name,
            action: onCreated ? { label: "Open", onClick: () => onCreated(record, { openDetail: true }) } : undefined,
          });
        }
        onCreated?.(record, { openDetail: false });
        onClose();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Batch Item 4 (2026-02): contacts.create surfaces a 409 with the existing contact's
      // id/name — warn instead of silently creating a duplicate, with a one-click way to open
      // the existing record instead (the modal stays open in case this was just a mistaken email).
      if (detail?.code === "duplicate_email" && detail?.existing_contact) {
        toast.error(`A contact with this email already exists: "${detail.existing_contact.name}"`, {
          description: "Change the email, or open the existing record instead.",
          action: onCreated ? { label: "Open existing", onClick: () => { onCreated(detail.existing_contact, { openDetail: true }); onClose(); } } : undefined,
        });
      } else if (detail?.code === "permission_denied") {
        // Backlog fix (2026-02): was showing the raw backend code "permission_denied" verbatim
        // in the toast — same class of raw-error-code issue seen elsewhere in the app. The scope
        // name is still surfaced (helps a founder/PM diagnose which permission is missing)
        // without exposing the internal code string to the end user.
        toast.error(`You don't have permission to ${isEdit ? "save changes to" : "create"} this ${entityLabel.toLowerCase()}`, {
          description: detail.scope ? `Missing permission: ${detail.scope}` : "Contact a founder/partner if this seems wrong.",
        });
      } else if (detail?.code === "conflict") {
        // Perf/copy fix (2026-09, NJM OS Phase-0 audit finding): was showing the raw code
        // "conflict" verbatim as the toast title — same class of issue as permission_denied
        // above. This 409 means someone else saved this record since the form was opened; the
        // modal deliberately stays open (values untouched) so a retry is a re-click, not a
        // re-type.
        toast.error("This record was updated by someone else. Your changes have not been saved.", {
          description: "Close this form to see the latest version, then re-apply your edit.",
        });
      } else if (detail?.code === "notion_write_failed" && detail?.error === "no_remote_id") {
        const reason = initialSyncInfo?.last_error ? (formatNotionError(initialSyncInfo.last_error) || `Reason: ${initialSyncInfo.last_error}`) : null;
        toast.error(`This ${entityLabel.toLowerCase()} is not in Notion yet`, {
          description: reason
            ? `${reason}. ${canRetrySync ? "Retry sync, then save your edit again." : "Ask a founder to retry the Notion sync, then save again."}`
            : (canRetrySync ? "Retry sync, then save your edit again." : "Ask a founder to retry the Notion sync, then save again."),
          action: canRetrySync ? { label: "Retry sync now", onClick: retrySyncNow } : undefined,
        });
      } else if (detail?.code === "notion_write_failed") {
        toast.error("Couldn't save changes — Notion didn't confirm the write", {
          description: "Your edits are still in this form — click Save to try again.",
        });
      } else if (detail?.code === "pm_required") {
        // PM Ownership Model (2026-09, Phase A): active-tier statuses (Won → Post) require a
        // Project Manager. Modal stays open, values untouched — pick a PM above and Save again.
        toast.error("A Project Manager is required for this status", {
          description: detail.message || "Pick a Project Manager above, then save again.",
        });
      } else if (detail?.code === "invalid_pm") {
        toast.error("That person can't be assigned as Project Manager", {
          description: "Pick a founder, partner, PM or coordinator from the list.",
        });
      } else {
        toast.error(detail?.error || detail?.code || `Couldn't ${isEdit ? "save changes" : "create record"}`, detail?.error ? { description: detail.code } : undefined);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={`record-modal-${type}`}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid={`record-modal-${type}-backdrop`} />
      <div className="relative w-full max-w-md h-full bg-[#FFFFFF] border-l border-[#E7E5E4] overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between border-b border-[#E7E5E4] px-5 py-4 sticky top-0 bg-[#FFFFFF] z-10">
          <h2 className="font-editorial text-xl tracking-tight">{isEdit ? `Edit ${entityLabel}` : cfg.title}</h2>
          <button data-testid={`record-modal-${type}-close`} onClick={onClose} className="text-[#A8A29E] hover:text-[#1C1C1E]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {isEdit && (
            <NotionSyncBanner sync={initialSyncInfo} role={user?.role} recordId={recordId} testIdPrefix="record-modal-notion-sync" />
          )}
          {cfg.needsClient && (
            <div>
              <label className={labelCls}>Client<span className="text-[#B45309]"> *</span></label>
              {showLockedClient ? (
                <div className="mt-1 rounded-md border border-[#E7E5E4] bg-[#FFFFFF]/60 px-3 py-2 text-sm text-[#57534E]" data-testid={`record-modal-${type}-client-locked`}>
                  {defaultClientName || "Selected client"}
                </div>
              ) : (
                <select data-testid={`record-modal-${type}-client-select`} value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls}>
                  <option value="">Select client…</option>
                  {clients.length === 0 && isEdit && initialClientId && <option value={initialClientId}>{initialClientName || "Current client"}</option>}
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>
          )}

          {cfg.fields.filter((f) => (!f.editOnly || isEdit) && (!f.showIf || f.showIf(values))).map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}{f.required && <span className="text-[#B45309]"> *</span>}</label>
              {f.kind === "text" && (
                <input data-testid={`record-modal-${type}-${f.key}`} value={values[f.key] || ""} placeholder={f.placeholder}
                  onChange={(e) => setField(f.key, e.target.value)} className={inputCls} />
              )}
              {f.kind === "url" && (
                <input type="url" data-testid={`record-modal-${type}-${f.key}`} value={values[f.key] || ""} placeholder={f.placeholder}
                  onChange={(e) => setField(f.key, e.target.value)} className={inputCls} />
              )}
              {f.kind === "number" && (
                <input data-testid={`record-modal-${type}-${f.key}`} type="number" value={values[f.key] || ""}
                  onChange={(e) => setField(f.key, e.target.value ? Number(e.target.value) : "")} className={inputCls} />
              )}
              {f.kind === "textarea" && (
                <textarea data-testid={`record-modal-${type}-${f.key}`} value={values[f.key] || ""} rows={3}
                  onChange={(e) => setField(f.key, e.target.value)} className={inputCls} />
              )}
              {f.kind === "date" && (
                <DatePicker testId={`record-modal-${type}-${f.key}`} value={values[f.key] || null}
                  onChange={(iso) => setField(f.key, iso)} />
              )}
              {f.kind === "checkbox" && (
                <label className="mt-1.5 inline-flex items-center gap-2 text-sm text-[#57534E]">
                  <input type="checkbox" data-testid={`record-modal-${type}-${f.key}`} checked={!!values[f.key]}
                    onChange={(e) => setField(f.key, e.target.checked)} className="h-4 w-4 rounded border-[#E7E5E4] accent-[#FFC817]" />
                  {values[f.key] ? "Yes" : "No"}
                </label>
              )}
              {f.kind === "daterange" && (
                <DateRangePicker testId={`record-modal-${type}-${f.key}`} value={values[f.key] || { start: null, end: null }}
                  onChange={(range) => setField(f.key, range)} />
              )}
              {f.kind === "select" && (
                <select data-testid={`record-modal-${type}-${f.key}`} value={values[f.key] || ""}
                  onChange={(e) => setField(f.key, e.target.value)} className={inputCls}>
                  <option value="">—</option>
                  {(options[f.key] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              {f.kind === "relation" && (
                <>
                  <select data-testid={`record-modal-${type}-${f.key}`} value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)} className={inputCls}>
                    <option value="">None</option>
                    {(relationOptions[f.relationEntity] || [])
                      .filter((o) => !f.scopeToClient || !clientId || o.client_id === clientId)
                      .map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  {f.allowQuickCreateContact && onRequestAddContact && (
                    <button type="button" data-testid={`record-modal-${type}-${f.key}-quick-create`}
                      onClick={() => onRequestAddContact(clientId)} disabled={!clientId}
                      className="mt-1.5 text-xs text-[#B45309] hover:underline disabled:opacity-40 disabled:no-underline">
                      + Person not listed? Add a new contact
                    </button>
                  )}
                </>
              )}
              {f.kind === "multiselect" && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`record-modal-${type}-${f.key}`}>
                  {(options[f.key] || []).map((o) => {
                    const active = (values[f.key] || []).includes(o);
                    return (
                      <button key={o} type="button" data-testid={`record-modal-${type}-${f.key}-${o}`} onClick={() => toggleMulti(f.key, o)}
                        className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${active ? "border-[#FFC817] bg-[#FFC817]/10 text-[#B45309]" : "border-[#E7E5E4] text-[#57534E] hover:border-[#A8A29E]"}`}>
                        {o}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-[#FFFFFF] border-t border-[#E7E5E4] px-5 pt-4 pb-20 flex gap-2">
          <button data-testid={`record-modal-${type}-cancel`} onClick={onClose} className="flex-1 rounded-md border border-[#E7E5E4] px-4 py-2 text-xs text-[#57534E] hover:text-[#1C1C1E]">
            Cancel
          </button>
          <button data-testid={`record-modal-${type}-save`} onClick={save} disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#FFC817] px-4 py-2 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {saving ? "Saving…" : isEdit ? "Save Changes" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
